import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";

import Button from "../../components/utils/Button";
import Table from "../../components/utils/Table";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import CardLayout from "../../components/utils/CardLayout";

const MAX_RECORDS = 5000;

const WiFiActivation = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [previewColumn, setPreviewColumn] = useState([]);
  const [totalUpload, setTotalUpload] = useState(0);
  const [validError, setValidError] = useState("");

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({
    mode: "onChange",
    defaultValues: {},
  });

  /**
   * Upload Excel file
   * File is only selected here.
   * Data will be read and shown in table after Submit.
   */
  const onFileUpload = (e) => {
    const file = e.target.files?.[0];

    // Clear previous data/errors
    setValidError("");
    setPreviewData([]);
    setPreviewColumn([]);
    setTotalUpload(0);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
      setValidError("Please upload a valid Excel file (.xlsx, .xls, .csv).");
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  /**
   * Submit
   * Read Excel file and show its data in table.
   */
  const onSubmit = () => {
    if (!selectedFile) {
      setValidError("Please upload an Excel file.");
      return;
    }

    const fileReader = new FileReader();

    fileReader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);

        const workbook = XLSX.read(data, {
          type: "array",
        });

        if (!workbook.SheetNames.length) {
          setValidError("Excel file does not contain any sheet.");
          return;
        }

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
        });

        // Empty file validation
        if (!jsonData.length) {
          setValidError("Excel file is empty.");
          setPreviewData([]);
          setPreviewColumn([]);
          setTotalUpload(0);
          return;
        }

        // Maximum record validation
        if (jsonData.length > MAX_RECORDS) {
          setValidError(
            `Maximum ${MAX_RECORDS} records are allowed.`
          );
          setPreviewData([]);
          setPreviewColumn([]);
          setTotalUpload(0);
          return;
        }

        // Get Excel headers
        const headers = Object.keys(jsonData[0] || {});

        // Required Wi-Fi Activation columns
        const requiredColumns = [
          "iccid",
          "msisdn",
          "street1",
          "street2",
          "city",
          "state",
          "zip",
        ];

        // Validate headers
        const isValid =
          headers.length === requiredColumns.length &&
          requiredColumns.every((column) =>
            headers.includes(column)
          );

        if (!isValid) {
          setValidError(
            "Invalid file. Required columns are missing or incorrect."
          );
          setPreviewData([]);
          setPreviewColumn([]);
          setTotalUpload(0);
          return;
        }

        // Create table columns
        const columns = headers.map((header) => ({
          displayName: header,
          key: header,
        }));

        // Set preview data
        setPreviewColumn(columns);
        setPreviewData(jsonData);
        setTotalUpload(jsonData.length);
        setValidError("");

        // Payload for API
        const payload = {
          vendorId: 101,
          simType: 26,
          data: jsonData,
        };

        console.log("WiFi Activation Payload:", payload);

        // API dispatch will be added here later
        // dispatch(wifiActivation(payload));
      } catch (error) {
        console.error("Excel parsing error:", error);

        setValidError(
          "Unable to read Excel file. Please check the file."
        );

        setPreviewData([]);
        setPreviewColumn([]);
        setTotalUpload(0);
      }
    };

    fileReader.onerror = () => {
      setValidError("Unable to read the selected file.");
    };

    fileReader.readAsArrayBuffer(selectedFile);
  };

  /**
   * Add key to each row for Table component
   */
  const rowData = useMemo(() => {
    if (!previewData?.length) {
      return [];
    }

    return previewData.map((item, index) => ({
      key: index + 1,
      ...item,
    }));
  }, [previewData]);

  /**
   * Download sample Excel
   */
  const downloadSampleFile = () => {
    const data = [
      {
        iccid: "11111111111111111111",
        msisdn: "1234567890",
        street1: "101 Main St",
        street2: "Ste 2",
        city: "Bellevue",
        state: "WA",
        zip: "11111",
      },
    ];

    downloadExcelSampleFile(
      data,
      "wifiactivation.xlsx"
    );
  };

  /**
   * Reset everything
   */
  const handleReset = () => {
    reset();

    setSelectedFile(null);
    setPreviewData([]);
    setPreviewColumn([]);
    setTotalUpload(0);
    setValidError("");

    const fileInput =
      document.getElementById("wifiExcelFile");

    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <CardLayout
      title="Wi-Fi Activation"
      icon="ri-wifi-line"
      rightContent={
        <Button
          title="Sample File"
          buttonType="button"
          icon={
            <i className="ri-download-cloud-line me-1" />
          }
          variant="secondary"
          style={{
            padding: "5px 10px",
          }}
          onClick={downloadSampleFile}
        />
      }
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className="row"
      >
        {/* File Upload */}
        <div className="col-md-12">
          <div
            className="border border-2 border-dashed rounded-4 p-3 text-center bg-light"
            style={{
              backgroundColor: "#f8f9fa",
            }}
          >
            <div className="row">
              <div className="col-md-6 offset-md-3">
                <label className="form-label fw-semibold">
                  Upload Excel File{" "}
                  <span className="text-danger">*</span>
                </label>

                <label
                  className="btn w-100 btn-outline-primary fw-semibold justify-content-center d-flex align-items-center"
                  style={{
                    minHeight: validError
                      ? "40px"
                      : "50px",
                    transition: "all .2s ease",
                  }}
                >
                  <i className="ri-upload-2-line me-2" />

                  {selectedFile
                    ? selectedFile.name
                    : "Upload Excel File"}

                  <input
                    id="wifiExcelFile"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    hidden
                    onChange={onFileUpload}
                  />
                </label>

                {validError && (
                  <div
                    style={{
                      color: "#dc3545",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {validError}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Buttons */}
         <div className="col-md-2 d-flex align-items-end" style={{ height: '70px' }}>
                   <div className="d-flex gap-3">
                     <Button
                       title="Submit"
                       buttonType="submit"
                       variant="primary"
                        icon={<i className="ri-send-plane-fill me-2"></i>}
                     />
                     <Button
                       title="Reset"
                       buttonType="button"
                       variant="secondary"
                       icon={<i className="ri-refresh-line me-2"></i>}
                       onClick={handleReset}
                     />
                   </div>
          </div>
        </div>

        {/* Preview Table */}
        {rowData.length > 0 && (
          <div className="col-12 mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0 fw-semibold">
                Upload Preview
              </h6>

              <span className="text-muted">
                Total Records: {totalUpload}
              </span>
            </div>

            <Table
              columns={previewColumn}
              loading={false}
              data={rowData}
              pagination={true}
              tableHeight="300px"
            />
          </div>
        )}
      </form>
    </CardLayout>
  );
};

export default WiFiActivation;
